import {
  generateNotificationId,
  type NotificationAddData,
  type NotificationData,
  type NotificationDismissState,
} from '@doxyme/ui/components';
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

type NotificationsSlice = {
  queue: NotificationData[];
};

// Used when a notification has a dismiss state - allowed the app to have context around why a notification was dismissed
const dismissNotificationHandlers: Map<
  string,
  NotificationAddData['callback']
> = new Map();

export const getInitialState = (): NotificationsSlice => ({
  queue: [],
});

const dismissNotification = createAsyncThunk(
  'notifications/dismissNotification',
  async (payload: { id: string; state?: NotificationDismissState }) => {
    const callback = dismissNotificationHandlers.get(payload.id);

    if (callback && payload.state) {
      await callback(payload.state);

      dismissNotificationHandlers.delete(payload.id);
    }
    return { id: payload.id };
  },
);

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: () => getInitialState(),
  reducers: {
    addNotification: (state, action: PayloadAction<NotificationAddData>) => {
      if (action.payload.id !== undefined) {
        const existingNotificationIndex = state.queue.findIndex(
          (notification) => notification.id === action.payload.id,
        );

        if (existingNotificationIndex !== -1) {
          state.queue[existingNotificationIndex] = {
            ...state.queue[existingNotificationIndex],
            ...action.payload,
            id: action.payload.id as string,
          };

          return;
        }
      }

      const id = action.payload.id ?? generateNotificationId();

      state.queue.push({
        id,
        type: action.payload.type,
        titlePlaceholderValues: action.payload.titlePlaceholderValues,
        icon: action.payload.icon,
        title: action.payload.title,
        buttonType: action.payload.buttonType,
        buttonText: action.payload.buttonText,
        buttonIsDisabled: action.payload.buttonIsDisabled,
        buttonTooltipText: action.payload.buttonTooltipText,
        isCloseable: action.payload.isCloseable,
      });

      if (action.payload.callback) {
        dismissNotificationHandlers.set(id, action.payload.callback);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(dismissNotification.fulfilled, (state, action) => {
      const { id } = action.payload;
      const notificationInQueue = state.queue.some(({ id: qid }) => qid === id);

      if (notificationInQueue) {
        state.queue = state.queue.filter(
          (notification) => notification.id !== id,
        );
      }
    });
  },
});

export const { addNotification } = notificationsSlice.actions;

export { dismissNotification };
